import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { TranslateService } from '@ngx-translate/core';
import { HttpProvider } from '../http/http';
import { DeviceProvider } from '../device/device';
import { PermissionProvider } from '../myria/permission-provider';
import { RouteProvider } from '../route/route';
import { SegmentProvider } from '../analytics/segment';
import { ResourceProvider } from '../resource/resource';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { ForeignIpGeolocationMapper } from './foreign-ip-geolocation.mapper';
import { ForeignIpGeolocationSession, isValidCoordinatePair } from './foreign-ip-geolocation-session';
import { ICustomerForeignIpInformation } from '../../model/models/customer/customer-foreign-ip-information.interface';
import { ICustomer } from '../../model/models/customer/customer.interface';
import { IGeolocation } from '../../model/models/device/geolocation.interface';
import { AnalyticsEventType, ForeignIpIssueName, SegmentLocationNames } from '../../model/types/analytics.const';
import { ForeignIpEvaluationReasonType, SANCTIONED_LOCATION_REASONS } from '../../model/types/foreign-ip-evaluation-reason-type';
import {
  ForeignIpBlockedScreenKind,
  ForeignIpGeolocationScreen,
  ForeignIpGeolocationScreenAction,
  ForeignIpVerdict,
  GeolocationCurrentPermission,
  GeolocationOutcome,
  GeolocationPermissionStatus,
  GeolocationPromptType,
  IForeignIpGeolocationScreenResult,
} from '../../model/types/geolocation-compliance-type';
import { APP_CONFIG } from '../../environment';

export interface IGeolocationPromptResult {
  outcome: GeolocationOutcome;
  status: GeolocationPermissionStatus;
}

export interface IForeignIpIdentificationParams {
  deviceTimeZone?: string;
  latitude?: number;
  longitude?: number;
}

export const SETTINGS_LOOP_MAX_ATTEMPTS = 10;
export const RESUME_TIMEOUT_MS = 5 * 60 * 1000;
export const SCREEN_PRESENT_TIMEOUT_MS = 10000;

@Injectable()
export class ForeignIpGeolocationProvider {

  constructor(
    private httpProvider: HttpProvider,
    private deviceProvider: DeviceProvider,
    private permissionProvider: PermissionProvider,
    private routeProvider: RouteProvider,
    private analytics: SegmentProvider,
    private resourceProvider: ResourceProvider,
    private translate: TranslateService,
    private platform: Platform
  ) { }

  getDeviceTimeZone(): string | null {
    const tz = this.deviceProvider.getDeviceTimeZone();
    if (!tz) {
      LoggerProvider.warning(this, LoggerEvent.CordovaGeolocation, 'Device time zone unavailable (Intl)');
    }
    return tz;
  }

  async readFix(force = false): Promise<IGeolocation | null> {
    if (!force) {
      const recent = ForeignIpGeolocationSession.getRecentFix();
      if (recent) {
        return recent;
      }
      if (ForeignIpGeolocationSession.pendingFix) {
        return ForeignIpGeolocationSession.pendingFix.then((pending) => isValidCoordinatePair(pending) ? pending : null);
      }
    }
    const fix = await ForeignIpGeolocationSession.trackPendingFix(this.deviceProvider.getGeolocation());
    return isValidCoordinatePair(fix) ? fix : null;
  }

  async fetchVerdict(coords: IGeolocation = null): Promise<ICustomerForeignIpInformation | null> {
    const params: IForeignIpIdentificationParams = {};
    const tz = this.getDeviceTimeZone();
    if (tz) {
      params.deviceTimeZone = tz;
    }
    if (isValidCoordinatePair(coords)) {
      params.latitude = coords.latitude;
      params.longitude = coords.longitude;
    }
    try {
      const response = await this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}identification/foreignipinformationforcustomer`, params);
      if (!response || response === '') {
        LoggerProvider.info(this, 'Foreign IP verdict: empty body (204) - no restriction');
        return null;
      }
      const info = ForeignIpGeolocationMapper.fromEntity(response);
      if (info) {
        LoggerProvider.info(this, 'Foreign IP verdict', { blocked: info.isTransactionBlockDueToForeignRestrictions, reasons: info.foreignIpEvaluationReasons });
      }
      return info;
    } catch (ex) {
      if (ex && ex.status === 400) {
        LoggerProvider.error(this, LoggerEvent.CommunicationError, 'Foreign IP verdict rejected (400) - check coordinate pair / range / token claims', ex);
      } else {
        LoggerProvider.exception(this, ex, LoggerEvent.CommunicationError, 'Foreign IP verdict request failed');
      }
      return null;
    }
  }

  classify(info: ICustomerForeignIpInformation | null): ForeignIpVerdict {
    if (!info) {
      return ForeignIpVerdict.Allow;
    }

    const reasons = info.foreignIpEvaluationReasons || [];
    const has = (reason: string) => reasons.indexOf(reason) >= 0;

    if (info.isTransactionBlockDueToForeignRestrictions) {
      if (reasons.length === 0) {
        LoggerProvider.warning(this, LoggerEvent.SendMoneyPage, 'Foreign IP blocked flag set without reasons; contract says no block fires');
        return ForeignIpVerdict.Allow;
      }
      const hasSanctionEvidence = reasons.some((r) => SANCTIONED_LOCATION_REASONS.indexOf(r) >= 0);
      if (hasSanctionEvidence) {
        return ForeignIpVerdict.HardBlockSanctioned;
      }
      if (has(ForeignIpEvaluationReasonType.SignInIpIsForeignAndUserForeignStreakExceededMonths)) {
        return ForeignIpVerdict.HardBlockStreak;
      }
      if (has(ForeignIpEvaluationReasonType.DeviceTimeZoneIsOnSanctionedTimeZonesListGeolocationRequired)) {
        return ForeignIpVerdict.PromptMandatory;
      }
      LoggerProvider.warning(this, LoggerEvent.SendMoneyPage, `Foreign IP blocked with unmapped reasons: ${reasons.join(',')}`);
      return ForeignIpVerdict.Allow;
    }

    if (has(ForeignIpEvaluationReasonType.SkipGeolocationCheck)) {
      return ForeignIpVerdict.PromptSkippable;
    }

    return ForeignIpVerdict.Allow;
  }

  isHardBlock(verdict: ForeignIpVerdict): boolean {
    return verdict === ForeignIpVerdict.HardBlockSanctioned
      || verdict === ForeignIpVerdict.HardBlockStreak;
  }

  async checkAtCalculator(customer: ICustomer, onBlockedDismiss: () => void): Promise<boolean> {
    const info = customer ? customer.customerForeignIpInformation : null;
    const verdict = this.classify(info);
    LoggerProvider.info(this, `Foreign IP geolocation: profile verdict ${verdict}`);

    if (this.isHardBlock(verdict)) {
      await this.showBlockedModal(verdict, onBlockedDismiss);
      return false;
    }
    if (verdict === ForeignIpVerdict.Allow) {
      return true;
    }
    if (verdict === ForeignIpVerdict.PromptSkippable && ForeignIpGeolocationSession.skippedOptionalPrompt) {
      LoggerProvider.info(this, 'Foreign IP geolocation: optional prompt already skipped in this calculator visit');
      return true;
    }

    if (!this.platform.is('cordova')) {
      LoggerProvider.info(this, 'Foreign IP geolocation: browser build, time zone only');
      return true;
    }

    const status = await this.permissionProvider.getLocationAuthorizationStatus();
    if (status === GeolocationPermissionStatus.Granted) {
      return this.silentRecheck(customer, onBlockedDismiss);
    }
    if (verdict === ForeignIpVerdict.PromptMandatory) {
      return this.runMandatoryFlow(customer, onBlockedDismiss);
    }
    return this.runSkippableFlow(customer, onBlockedDismiss);
  }

  private async silentRecheck(customer: ICustomer, onBlockedDismiss: () => void): Promise<boolean> {
    const fix = await this.readFix(false);
    if (!fix) {
      LoggerProvider.warning(this, LoggerEvent.CordovaGeolocation, 'Permission granted but no GPS fix for the silent re-check; proceeding');
      return true;
    }
    if (this.isMockFix(fix)) {
      return this.blockForMockGps();
    }
    return this.recheckWithFix(customer, fix, onBlockedDismiss);
  }

  isMockFix(fix: IGeolocation): boolean {
    return !!fix && fix.isMock === true && this.platform.is('android');
  }

  private async blockForMockGps(): Promise<boolean> {
    LoggerProvider.warning(this, LoggerEvent.CordovaGeolocation, 'Mock GPS detected; coordinates withheld and the transaction blocked');
    this.trackRejected(ForeignIpIssueName.MockGps);
    await this.openScreen(ForeignIpGeolocationScreen.MockGps);
    return false;
  }

  private async recheckWithFix(customer: ICustomer, fix: IGeolocation, onBlockedDismiss: () => void): Promise<boolean> {
    const info = await this.fetchVerdict(fix);
    this.applyVerdict(customer, info);
    const verdict = this.classify(info);
    LoggerProvider.info(this, `Foreign IP geolocation: verdict with coordinates ${verdict}`);
    if (this.isHardBlock(verdict)) {
      await this.showBlockedModal(verdict, onBlockedDismiss);
      return false;
    }
    return true;
  }

  private applyVerdict(customer: ICustomer, info: ICustomerForeignIpInformation | null) {
    if (customer && info) {
      customer.customerForeignIpInformation = info;
    }
  }

  async runMandatoryFlow(customer: ICustomer = null, onBlockedDismiss: () => void = null): Promise<boolean> {
    const result = await this.runPromptAttempt(GeolocationPromptType.Mandatory);
    if (result.outcome !== GeolocationOutcome.Granted) {
      return false;
    }
    return this.verifyAfterGrant(customer, onBlockedDismiss);
  }

  async runSkippableFlow(customer: ICustomer = null, onBlockedDismiss: () => void = null): Promise<boolean> {
    const result = await this.runPromptAttempt(GeolocationPromptType.Skippable);
    if (result.outcome === GeolocationOutcome.Granted) {
      return this.verifyAfterGrant(customer, onBlockedDismiss);
    }
    if (result.outcome === GeolocationOutcome.Skipped) {
      ForeignIpGeolocationSession.skippedOptionalPrompt = true;
    }
    return true;
  }

  private async verifyAfterGrant(customer: ICustomer, onBlockedDismiss: () => void): Promise<boolean> {
    const fix = await this.readFix(true);
    if (!fix) {
      LoggerProvider.warning(this, LoggerEvent.CordovaGeolocation, 'Permission granted but no GPS fix; validate will re-check');
      return true;
    }
    if (this.isMockFix(fix)) {
      return this.blockForMockGps();
    }
    return this.recheckWithFix(customer, fix, onBlockedDismiss);
  }

  private async runPromptAttempt(promptType: GeolocationPromptType): Promise<IGeolocationPromptResult> {
    if (ForeignIpGeolocationSession.promptInProgress) {
      return ForeignIpGeolocationSession.promptInProgress;
    }
    const attempt = this.executePromptAttempt(promptType).then((result) => {
      ForeignIpGeolocationSession.promptInProgress = null;
      return result;
    }, (ex) => {
      ForeignIpGeolocationSession.promptInProgress = null;
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Geolocation prompt attempt failed');
      return { outcome: GeolocationOutcome.Denied, status: GeolocationPermissionStatus.Unknown };
    });
    ForeignIpGeolocationSession.promptInProgress = attempt;
    return attempt;
  }

  private async executePromptAttempt(promptType: GeolocationPromptType): Promise<IGeolocationPromptResult> {
    const initialStatus = await this.permissionProvider.getLocationAuthorizationStatus();
    this.trackPrompted(promptType, ForeignIpGeolocationProvider.toCurrentPermission(initialStatus));

    const result = await this.promptForStatus(promptType, initialStatus);
    this.trackCompleted(promptType, result.outcome);
    return result;
  }

  private async promptForStatus(promptType: GeolocationPromptType, initialStatus: GeolocationPermissionStatus): Promise<IGeolocationPromptResult> {
    if (ForeignIpGeolocationProvider.isSettingsOnly(initialStatus)) {
      return this.promptWhenPermanentlyDenied(promptType);
    }
    return this.promptWithNativeDialog(promptType);
  }

  private async promptWithNativeDialog(promptType: GeolocationPromptType): Promise<IGeolocationPromptResult> {
    const action = await this.openScreen(ForeignIpGeolocationProvider.screenFor(promptType));
    if (action !== 'continue' && action !== 'settings') {
      return ForeignIpGeolocationProvider.declined(promptType, await this.permissionProvider.getLocationAuthorizationStatus());
    }

    let status = await this.permissionProvider.requestLocationAuthorizationDetailed();
    if (status === GeolocationPermissionStatus.Granted) {
      return ForeignIpGeolocationProvider.granted(status);
    }
    if (promptType === GeolocationPromptType.Skippable) {
      return ForeignIpGeolocationProvider.declined(promptType, status);
    }

    if (status === GeolocationPermissionStatus.DeniedOnce) {
      const retryAction = await this.openScreen(ForeignIpGeolocationScreen.Rationale);
      if (retryAction !== 'continue') {
        return ForeignIpGeolocationProvider.declined(promptType, status);
      }
      status = await this.permissionProvider.requestLocationAuthorizationDetailed();
      if (status === GeolocationPermissionStatus.Granted) {
        return ForeignIpGeolocationProvider.granted(status);
      }
    }

    status = await this.runSettingsLoop(status);
    return ForeignIpGeolocationProvider.resultFor(promptType, status);
  }

  private async promptWhenPermanentlyDenied(promptType: GeolocationPromptType): Promise<IGeolocationPromptResult> {
    if (promptType === GeolocationPromptType.Mandatory) {
      const settingsStatus = await this.runSettingsLoop(await this.permissionProvider.getLocationAuthorizationStatus());
      return ForeignIpGeolocationProvider.resultFor(promptType, settingsStatus);
    }
    const action = await this.openScreen(ForeignIpGeolocationScreen.Skippable);
    const status = await this.permissionProvider.getLocationAuthorizationStatus();
    if (action === 'continue' && status === GeolocationPermissionStatus.Granted) {
      return ForeignIpGeolocationProvider.granted(status);
    }
    return { outcome: GeolocationOutcome.Skipped, status };
  }

  private async runSettingsLoop(currentStatus: GeolocationPermissionStatus): Promise<GeolocationPermissionStatus> {
    let status = currentStatus;
    for (let attempt = 0; attempt < SETTINGS_LOOP_MAX_ATTEMPTS; attempt++) {
      const action = await this.openScreen(ForeignIpGeolocationScreen.Settings);
      if (action !== 'settings') {
        return this.permissionProvider.getLocationAuthorizationStatus();
      }
      const opened = await this.permissionProvider.openLocationSettings(status);
      if (!opened) {
        LoggerProvider.warning(this, LoggerEvent.CordovaGeolocation, 'Device settings could not be opened; ending the settings loop');
        return this.permissionProvider.getLocationAuthorizationStatus();
      }
      await this.waitForResume();
      status = await this.permissionProvider.getLocationAuthorizationStatus();
      if (status === GeolocationPermissionStatus.Granted) {
        return status;
      }
    }
    return this.permissionProvider.getLocationAuthorizationStatus();
  }

  private async waitForResume(): Promise<void> {
    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) {
          return;
        }
        done = true;
        if (subscription && !subscription.closed) {
          subscription.unsubscribe();
        }
        window.clearTimeout(timeout);
        resolve();
      };
      const subscription = this.platform.resume.subscribe(() => finish());
      const timeout = window.setTimeout(finish, RESUME_TIMEOUT_MS);
    });
  }

  private async openScreen(screen: ForeignIpGeolocationScreen): Promise<ForeignIpGeolocationScreenAction> {
    return new Promise<ForeignIpGeolocationScreenAction>((resolve) => {
      let resolved = false;
      let presentWatchdog: number = null;
      const finish = (action: ForeignIpGeolocationScreenAction) => {
        if (resolved) {
          return;
        }
        resolved = true;
        window.clearTimeout(presentWatchdog);
        resolve(action);
      };

      const opened = this.routeProvider.openModal(this.routeProvider.pages.ForeignIpGeolocationPage, { screen }, (result: IForeignIpGeolocationScreenResult) => {
        finish(result && result.action ? result.action : 'back');
      });
      if (!opened) {
        LoggerProvider.warning(this, LoggerEvent.SendMoneyPage, `Foreign IP screen ${screen} was not opened (navigation skipped); treating as back`);
        finish('back');
        return;
      }

      presentWatchdog = window.setTimeout(() => {
        LoggerProvider.error(this, LoggerEvent.SendMoneyPage, `Foreign IP screen ${screen} did not present within ${SCREEN_PRESENT_TIMEOUT_MS} ms; treating as back`);
        finish('back');
      }, SCREEN_PRESENT_TIMEOUT_MS);
      Promise.resolve(opened).then(() => window.clearTimeout(presentWatchdog), () => finish('back'));
    });
  }

  describeBlock(verdict: ForeignIpVerdict): { kind: ForeignIpBlockedScreenKind, issueName: ForeignIpIssueName } {
    if (verdict === ForeignIpVerdict.HardBlockStreak) {
      return { kind: ForeignIpBlockedScreenKind.AccountRestricted, issueName: ForeignIpIssueName.NineMonthRestriction };
    }
    return { kind: ForeignIpBlockedScreenKind.TransferNotAvailable, issueName: ForeignIpIssueName.SanctionedCountry };
  }

  async showBlockedModal(verdict: ForeignIpVerdict, onDismiss: () => void): Promise<void> {
    const { kind, issueName } = this.describeBlock(verdict);
    this.trackRejected(issueName);

    const countryCode = APP_CONFIG.region.defaultCountryFrom;
    let countryName = countryCode;
    try {
      const country = await this.resourceProvider.getCountryByValue(countryCode);
      if (country && country.countryName) {
        countryName = country.countryName;
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to resolve country name for foreign IP modal');
    }
    const translationParams: { country: string } = { country: countryName };

    this.routeProvider.replace(this.routeProvider.pages.ModalAlertPage, {
      showFullPage: true,
      img: 'assets/imgs/warning-circle-background.svg',
      title: this.translate.instant(`sendMoney.foreignIP.${kind}.title`, translationParams),
      msg: this.translate.instant(`sendMoney.foreignIP.${kind}.body`, translationParams),
      primaryButtonText: this.translate.instant(`sendMoney.foreignIP.${kind}.button`),
      showCircle: false,
      newFooterStyle: true,
      newDesign: true,
      showRightClose: false,
      showHelp: false,
      showBack: false,
      hideHeader: true,
      verticalCenter: true,
      primaryAction: () => {
        if (onDismiss) {
          onDismiss();
        }
      },
    });
  }

  private static toCurrentPermission(status: GeolocationPermissionStatus): GeolocationCurrentPermission {
    if (status === GeolocationPermissionStatus.NotRequested || status === GeolocationPermissionStatus.Unknown) {
      return GeolocationCurrentPermission.NotRequested;
    }
    return GeolocationCurrentPermission.Denied;
  }

  private static isSettingsOnly(status: GeolocationPermissionStatus): boolean {
    return status === GeolocationPermissionStatus.DeniedAlways || status === GeolocationPermissionStatus.ServicesDisabled;
  }

  private static screenFor(promptType: GeolocationPromptType): ForeignIpGeolocationScreen {
    if (promptType === GeolocationPromptType.Skippable) {
      return ForeignIpGeolocationScreen.Skippable;
    }
    return ForeignIpGeolocationScreen.Mandatory;
  }

  private static granted(status: GeolocationPermissionStatus): IGeolocationPromptResult {
    return { outcome: GeolocationOutcome.Granted, status };
  }

  private static declined(promptType: GeolocationPromptType, status: GeolocationPermissionStatus): IGeolocationPromptResult {
    if (promptType === GeolocationPromptType.Skippable) {
      return { outcome: GeolocationOutcome.Skipped, status };
    }
    return { outcome: GeolocationOutcome.Denied, status };
  }

  private static resultFor(promptType: GeolocationPromptType, status: GeolocationPermissionStatus): IGeolocationPromptResult {
    if (status === GeolocationPermissionStatus.Granted) {
      return ForeignIpGeolocationProvider.granted(status);
    }
    return ForeignIpGeolocationProvider.declined(promptType, status);
  }

  private trackPrompted(promptType: GeolocationPromptType, currentPermission: GeolocationCurrentPermission) {
    this.analytics.trackEvent({ eventName: AnalyticsEventType.GeolocationPrompted, analyticsData: { promptType, currentPermission } })
      .catch(() => LoggerProvider.error(this, LoggerEvent.GeneralException, 'Failed to track Geolocation Prompted'));
  }

  private trackCompleted(promptType: GeolocationPromptType, outcome: GeolocationOutcome) {
    this.analytics.trackEvent({ eventName: AnalyticsEventType.GeolocationCompleted, analyticsData: { promptType, outcome } })
      .catch(() => LoggerProvider.error(this, LoggerEvent.GeneralException, 'Failed to track Geolocation Completed'));
  }

  trackRejected(issueName: ForeignIpIssueName | string) {
    if (ForeignIpGeolocationSession.rejectionTracked) {
      return;
    }
    ForeignIpGeolocationSession.rejectionTracked = true;
    this.analytics.trackEvent({ eventName: AnalyticsEventType.TransactionRejectedByCompliance, analyticsData: {
      rejectionSource: 'Foreign IP Control',
      issueName,
      location: SegmentLocationNames.SendMoney
    }}).catch(() => LoggerProvider.error(this, LoggerEvent.GeneralException, 'Failed to track Transaction Rejected By Compliance'));
  }
}



// WEBPACK FOOTER //
// ./src/providers/foreign-ip-geolocation/foreign-ip-geolocation.ts